/**
 * Homepage entry — search-first layout when HOMEPAGE_SEARCH_FIRST_ENABLED=true,
 * otherwise the legacy brochure homepage.
 *
 * Search-first is the production default. We render it eagerly and optimistically so a short
 * spinner does not park the sticky footer in the Lighthouse viewport (major CLS).
 */
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import HomePageSearchFirst from '../components/homepage/HomePageSearchFirst';
import { PUBLIC_SHELL_MIN_HEIGHT } from '../constants/clsShell';
import { loadHomepageLayoutOnce } from '../utils/homepageLayoutLoader';

const HomePageLegacy = lazy(() => import('./HomePageLegacy'));

/** Keep footer below the fold while any homepage shell is resolving. */
const HomepageShellFallback: React.FC<{ label: string }> = ({ label }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      pt: 12,
      minHeight: PUBLIC_SHELL_MIN_HEIGHT,
    }}
  >
    <CircularProgress aria-label={label} />
  </Box>
);

function readCachedSearchFirst(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const cached = window.sessionStorage.getItem('apt_homepage_search_first');
    if (cached === '0') return false;
    if (cached === '1') return true;
  } catch {
    // ignore
  }
  return true;
}

function writeCachedSearchFirst(enabled: boolean): void {
  try {
    window.sessionStorage.setItem('apt_homepage_search_first', enabled ? '1' : '0');
  } catch {
    // ignore
  }
}

const HomePage: React.FC = () => {
  const viteOverride = import.meta.env.VITE_HOMEPAGE_SEARCH_FIRST_ENABLED;
  const [searchFirst, setSearchFirst] = useState<boolean>(() => {
    if (viteOverride === 'false') return false;
    if (viteOverride === 'true') return true;
    return readCachedSearchFirst();
  });

  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      // Vite pin skips the layout flag probe (prod builds should set true).
      if (viteOverride === 'true' || viteOverride === 'false') {
        return;
      }
      const json = await loadHomepageLayoutOnce();
      if (!cancelled && json?.data) {
        const enabled = Boolean(json.data.searchFirstEnabled);
        writeCachedSearchFirst(enabled);
        setSearchFirst(enabled);
      }
    };
    void resolve();
    return () => {
      cancelled = true;
    };
  }, [viteOverride]);

  if (!searchFirst) {
    return (
      <Suspense fallback={<HomepageShellFallback label="Loading homepage" />}>
        <HomePageLegacy />
      </Suspense>
    );
  }

  return <HomePageSearchFirst />;
};

export default HomePage;
